/**
 * Wachtrij van één voor de bestelknop van de Vinyl Lettering Designer.
 *
 * De host roept eerst onDesignReady (bestand -> React-state) en meteen daarna
 * onRequestAddToCart. Die state is dan nog niet verwerkt, dus het verzoek wacht
 * hier tot de configurator klaar is (`step` vanuit een effect, elke render):
 * bestand van DIT ontwerp, prijs klaar, en de quote hoort bij de HUIDIGE
 * configuratie (M1). Dan wordt toegevoegd met de nieuwste functie.
 *
 * Regels:
 * - Een verzoek terwijl het vorige nog TOEVOEGT: het nieuwe weigert met `busy`;
 *   het lopende wordt gewoon afgerond (I2).
 * - Een verzoek terwijl het vorige nog WACHT: het vorige vervalt (`failed`).
 * - Elke afronding hoort bij zijn eigen verzoek: een laat antwoord van een ouder
 *   verzoek raakt een nieuwer verzoek nooit.
 * - Prijsfout terwijl het verzoek wacht: meteen weigeren met die fout.
 * - Na `timeoutMs`: weigeren met `timeout`, maar alleen zolang het verzoek WACHT.
 *   Loopt het toevoegen al, dan stopt de klok: anders ziet de klant een fout en
 *   probeert hij opnieuw terwijl de eerste regel nog binnenkomt (eindreview M3).
 * - Beschrijft de configuratie (maat, kleur, ontwerp-hash) een ander ontwerp dan
 *   het geregistreerde bestand (klant paste het ontwerp aan terwijl het toevoegen
 *   liep): weigeren met `failed`, nooit het oude bestand met de nieuwe maat en
 *   prijs in de winkelwagen (gevonden in de e2e, Task 6).
 * Los van React zodat het te testen is.
 */

export interface DesignerOrderState {
  uploadedFileId: string | null | undefined;
  isPriceReady: boolean;
  isPriceLoading: boolean;
  selectionCheckPending: boolean;
  configurationError: string | null;
  /** De laatste quote is opgehaald voor precies de huidige configuratie. */
  quoteMatchesConfiguration: boolean;
  /** Maat (mm) en kleur die de huidige configuratie bestelt; ontbreekt = niet vergelijken. */
  currentDesign?: DesignerOrderDesign | null;
}

/** Wat een bestand of configuratie bestelt: vel in mm, de kleurwaarde en de ontwerp-hash. */
export interface DesignerOrderDesign {
  widthMm: number;
  heightMm: number;
  materialValue: string;
  /**
   * `vstDesignHash` van het ontwerp (host: bij elke prijsaanvraag en bij de registratie).
   * Vangt ook een wijziging die maat en kleur gelijk laat (andere tekst of letter).
   * Ontbreekt hij aan een kant, dan alleen maat en kleur.
   */
  designHash?: string | null;
}

/** Speling voor afronding (host rekent in 0,1 cm, de server in hele mm). */
const MAAT_SPELING_MM = 2;

function zelfdeOntwerp(bestand: DesignerOrderDesign, huidig: DesignerOrderDesign) {
  return Math.abs(bestand.widthMm - huidig.widthMm) <= MAAT_SPELING_MM
    && Math.abs(bestand.heightMm - huidig.heightMm) <= MAAT_SPELING_MM
    && (!bestand.materialValue || bestand.materialValue === huidig.materialValue)
    && (!bestand.designHash || !huidig.designHash || bestand.designHash === huidig.designHash);
}

interface Verzoek {
  uploadId: string;
  bestand: DesignerOrderDesign | null;
  resolve: () => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
  busy: boolean;
}

export function createDesignerOrderGate(opts: {
  timeoutMs: number;
  /** Altijd de meldingen van de huidige taal (wordt bij gebruik gelezen). */
  messages: () => { busy: string; failed: string; timeout: string };
  /** Het wachtende uploadId (of null), voor React-state die het effect opnieuw laat lopen. */
  onChange: (uploadId: string | null) => void;
  /**
   * Het toevoegen gooide (geen verwachte weigering met reden). De configurator meldt
   * het aan Sentry; deze module blijft zonder imports (eindreview I1).
   */
  onRunError?: (error: unknown) => void;
}) {
  let huidig: Verzoek | null = null;

  function afronden(verzoek: Verzoek, reden?: string) {
    // Alleen het eigen verzoek: een ouder antwoord mag een nieuwer verzoek niet afronden.
    if (huidig !== verzoek) return;
    huidig = null;
    clearTimeout(verzoek.timer);
    opts.onChange(null);
    if (reden === undefined) verzoek.resolve();
    else verzoek.reject(new Error(reden || opts.messages().failed));
  }

  return {
    request(uploadId: string, bestand?: DesignerOrderDesign | null): Promise<void> {
      return new Promise<void>((resolve, reject) => {
        if (huidig?.busy) {
          reject(new Error(opts.messages().busy));
          return;
        }
        if (huidig) afronden(huidig, opts.messages().failed);
        const verzoek: Verzoek = {
          uploadId,
          bestand: bestand ?? null,
          resolve,
          reject,
          busy: false,
          timer: setTimeout(() => afronden(verzoek, opts.messages().timeout), opts.timeoutMs),
        };
        huidig = verzoek;
        opts.onChange(uploadId);
      });
    },

    step(state: DesignerOrderState, run: () => Promise<true | string>) {
      const verzoek = huidig;
      if (!verzoek || verzoek.busy) return;
      if (state.isPriceLoading || state.selectionCheckPending) return;
      if (state.configurationError) {
        afronden(verzoek, state.configurationError);
        return;
      }
      if (state.uploadedFileId !== verzoek.uploadId || !state.isPriceReady || !state.quoteMatchesConfiguration) return;
      if (verzoek.bestand && state.currentDesign && !zelfdeOntwerp(verzoek.bestand, state.currentDesign)) {
        afronden(verzoek, opts.messages().failed);
        return;
      }
      verzoek.busy = true;
      clearTimeout(verzoek.timer);
      run().then(
        (uitkomst) => afronden(verzoek, uitkomst === true ? undefined : uitkomst),
        (error) => {
          console.error('[vst-designer] toevoegen aan winkelwagen mislukt', error);
          try {
            opts.onRunError?.(error);
          } catch {
            // Melden mag het afronden niet tegenhouden.
          }
          afronden(verzoek, '');
        }
      );
    },

    /** Weg van de pagina: een wachtend of lopend verzoek weigert (de designer blijft niet hangen). */
    cancel() {
      if (huidig) afronden(huidig, opts.messages().failed);
    },
  };
}

export type DesignerOrderGate = ReturnType<typeof createDesignerOrderGate>;

/**
 * De fout die een wachtende designerbestelling meteen laat weigeren: de prijsfout
 * van de configurator, of een prijslimiet (429). Bij een 429 zet de configurator
 * geen configurationError (hij probeert later zelf opnieuw); zonder dit bleef de
 * bestelknop 20 s op "..." en kwam daarna de time-outmelding in plaats van de
 * 429-melding (gevonden in de e2e, Task 6). De klant probeert het daarna zelf opnieuw.
 */
export function designerOrderError(
  configurationError: string | null,
  rateLimit: { seconds: number; longWait: boolean } | null,
  messages: { rateLimited: string; rateLimitedSeconds: string }
): string | null {
  if (configurationError) return configurationError;
  if (!rateLimit) return null;
  return rateLimit.longWait
    ? messages.rateLimited
    : messages.rateLimitedSeconds.replace('{seconds}', String(rateLimit.seconds));
}
